import { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Button, Container, Dialog, DialogContent, makeStyles, Typography } from '@material-ui/core';
import { TescuchaLogo } from '../components/atoms/TescuchaLogo';
import { Footer } from '../components/Footer';
import { InputControlled } from '../components/atoms/InputControlled';
import FormularioService from '../API/FormularioService';
import { CLOUD_FLARE_CAPTCHA } from '../constants/network';
import analyticsService from '../API/analyticsService';

const Home = () => {
  const classes = useStyles();
  const history = useHistory();
  const [openConsultDialog, setOpenConsultDialog] = useState(false);

  const handleOpenConsultDialog = () => {
    analyticsService.sendEvent('BTN_SEGUIMIENTO_DENUNCIA');
    setOpenConsultDialog(true);
  };

  // Consultar el tipo de captcha y mostrar/ocultar el badge según corresponda
  useEffect(() => {
    const getCaptchaType = async () => {
      try {
        const captchaType = await FormularioService.getTypeCaptcha();

        // Función para ocultar el badge de Google reCAPTCHA
        const hideBadge = () => {
          const badge = document.querySelector('.grecaptcha-badge');
          if (badge) {
            badge.style.display = 'none';
            badge.style.visibility = 'hidden';
          }
        };

        // Función para mostrar el badge de Google reCAPTCHA
        const showBadge = () => {
          const badge = document.querySelector('.grecaptcha-badge');
          if (badge) {
            badge.style.display = 'block';
            badge.style.visibility = 'visible';
          }
        };

        // Si es Cloudflare, ocultar el badge de Google reCAPTCHA
        if (captchaType === CLOUD_FLARE_CAPTCHA) {
          // Usar MutationObserver para detectar cuando el badge se carga
          const observer = new MutationObserver(() => {
            hideBadge();
          });

          // Observar cambios en el body para detectar cuando se agrega el badge
          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });

          // Intentar ocultar inmediatamente
          hideBadge();

          // Intentar ocultar después de delays para asegurar que se oculte
          setTimeout(hideBadge, 500);
          setTimeout(hideBadge, 1000);
          setTimeout(hideBadge, 2000);

          // Limpiar el observer después de 5 segundos
          setTimeout(() => {
            observer.disconnect();
          }, 5000);
        } else {
          // Si no es Cloudflare (es Google reCAPTCHA), mostrar el badge
          // Usar MutationObserver para detectar cuando el badge se carga
          const observer = new MutationObserver(() => {
            showBadge();
          });

          // Observar cambios en el body para detectar cuando se agrega el badge
          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });

          // Intentar mostrar después de delays para asegurar que se muestre
          setTimeout(showBadge, 500);
          setTimeout(showBadge, 1000);
          setTimeout(showBadge, 2000);

          // Limpiar el observer después de 5 segundos
          setTimeout(() => {
            observer.disconnect();
          }, 5000);
        }
      } catch (error) {
        console.error('Error al consultar el tipo de captcha:', error);
      }
    };

    getCaptchaType();
  }, []);

  const onSubmit = values => {
    analyticsService.sendEvent('BTN_INGRESAR');
    history.push('/denuncia/consulta', { folio: values.folio, password: values.password });
  };

  const { dirty, handleSubmit, values, handleChange, handleBlur, isValid, setFieldValue } = useFormik({
    initialValues: {
      folio: '',
      password: '',
    },
    onSubmit,
    validationSchema: Yup.object().shape({
      folio: Yup.string().required('El número de folio es requerido'),
      password: Yup.string().required('La contraseña es requerida'),
    }),
    validateOnMount: true,
  });

  const handlePasteInput = event => {
    const pasteData = event.clipboardData.getData('Text');
    const [folioPart, passwordPart] = pasteData.split('Contraseña:');

    if (folioPart && passwordPart) {
      const folio = folioPart.replace(/Número de denuncia:|[\n\r\s]/g, '');
      const password = passwordPart.replace(/[\n\r\s]/g, '');
      setFieldValue('folio', folio);
      setFieldValue('password', password);
      event.preventDefault();
    }
  };

  useEffect(() => {
    analyticsService.sendEvent('HOME');
  }, []);

  return (
    <Container component="main" maxWidth="lg" className={classes.container}>
      <Box display="flex" alignItems="center" gridGap={20} flexWrap="wrap">
        <TescuchaLogo />
        <Typography variant="h4" component="h1" className={classes.title}>
          Sistema de Denuncias TEscucha
        </Typography>
      </Box>
      <Box my={1} className={classes.wrapper}>
        <Box className={classes.paper}>
          <Typography className={classes.text_1}>
            En{' '}
            <Typography component="span" className={classes.bold}>
              Grupo Truper
            </Typography>{' '}
            promovemos un entorno de trabajo basado en nuestros valores:
            <Typography className={classes.bold}>integridad, respeto, congruencia y profesionalismo.</Typography>
          </Typography>
          <Typography>
            Por eso creamos{' '}
            <Typography component="span" className={classes.bold}>
              TEscucha
            </Typography>
            , un sistema para que colaboradores, excolaboradores, clientes, proveedores y transportistas puedan reportar
            situaciones que vayan en contra de estos valores, tales como:
          </Typography>
          <ul className={classes.list}>
            <li>Acoso laboral</li>
            <li>Acoso sexual</li>
            <li>Comercialización, posesión o consumo de drogas</li>
            <li>Conflicto de intereses</li>
            <li>Discriminación</li>
            <li>Fraude o robo</li>
            <li>Soborno</li>
          </ul>
          <Typography>Todas las denuncias recibidas serán tratadas con profesionalismo.</Typography>
          <Typography>Tu participación contribuye para fortalecer nuestra cultura.</Typography>
        </Box>
        <Box className={`${classes.paper} ${classes.btnWrapper}`}>
          <Link
            to="/denuncia"
            style={{ textDecoration: 'none', width: '100%' }}
            onClick={() => analyticsService.sendEvent('BTN_DENUNCIA')}
          >
            <Button fullWidth variant="outlined" color="primary" className={classes.btn}>
              Realiza una denuncia
            </Button>
          </Link>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            className={classes.btn}
            onClick={handleOpenConsultDialog}
          >
            Da seguimiento a una denuncia
          </Button>
          <Link
            to="/lineamientos"
            style={{ textDecoration: 'none', width: '100%' }}
            onClick={() => analyticsService.sendEvent('BTN_LINEAMIENTOS_ETICA')}
          >
            <Button fullWidth variant="outlined" color="primary" className={classes.btn}>
              Conoce los lineamientos de ética
            </Button>
          </Link>
          <Link
            to="/preguntas-frecuentes"
            style={{ textDecoration: 'none', width: '100%' }}
            onClick={() => analyticsService.sendEvent('BTN_PREGUNTAS_FRECUENTES')}
          >
            <Button fullWidth variant="outlined" color="primary" className={classes.btn}>
              Consulta las preguntas frecuentes
            </Button>
          </Link>
        </Box>
      </Box>
      <Footer />

      <Dialog
        open={openConsultDialog}
        aria-labelledby="consult-dialog-title"
        aria-describedby="consult-dialog-description"
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
          },
        }}
        PaperProps={{
          style: {
            borderRadius: 16,
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
          },
        }}
      >
        <DialogContent className={classes.dialogContent}>
          <Box display="flex" flexDirection="column" gridGap={10}>
            <InputControlled
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Escribe aquí"
              label="Número de folio:"
              name="folio"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.folio}
              onPaste={handlePasteInput}
            />
            <InputControlled
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Escribe aquí"
              label="Contraseña:"
              name="password"
              type="password"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.password}
              onPaste={handlePasteInput}
            />
          </Box>
          <Box width="100%" display="flex" justifyContent="center" alignItems="center" gridGap={16} mt={3}>
            <Button
              variant="outlined"
              color="primary"
              className={classes.btn}
              onClick={() => setOpenConsultDialog(false)}
              fullWidth
            >
              Regresar
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              fullWidth
              disabled={!dirty || !isValid}
              onClick={handleSubmit}
            >
              Ingresar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  title: {
    fontWeight: 'bold',
  },
  wrapper: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  paper: {
    padding: theme.spacing(3),
    backgroundColor: '#fff',
    borderRadius: 8,
    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
  },
  btnWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(2),
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(5),
  },
  btn: {
    borderWidth: 2,
    textTransform: 'none',
    '&:hover': {
      borderWidth: 2,
    },
  },
  text_1: {
    marginBottom: theme.spacing(2),
  },
  bold: { fontWeight: 'bold' },
  list: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    '& li': {
      padding: theme.spacing(0, 0),
      lineHeight: 1,
    },
  },
  dialogContent: {
    padding: '24px',
    minWidth: '260px',
  },
}));

export default Home;
